/**
 * MiraCheatGuide — how-to for Operator Mira (WhatsApp + chat).
 */
import React from 'react';
import {
  MessageSquare, Sparkles, Search, Send, Lock, AlertTriangle, Edit3, Phone,
  RefreshCw,
} from 'lucide-react';
import CheatGuideShell, {
  Section, Btn, Pill, Warn, Note, Choice,
} from './CheatGuideShell';

export default function MiraCheatGuide() {
  return (
    <CheatGuideShell
      title="Cheat Guide — what is Operator Mira & how do I talk to her?"
      storageKey="sb_mira_cheat_open"
      testId="mira-cheat-guide">

      <Section title="What is Mira?" icon={<Sparkles size={13} />}>
        Mira is your in-house Operator-Brain.{' '}
        <strong>Two ways to use her:</strong>
        <ol className="space-y-1 list-decimal ml-5 mt-2">
          <li>
            <strong>Chat tab in ScaleBoard</strong> — natural language. Ask
            anything: "how is JLR Goa doing this month", "soul of Pulkit",
            "compare JLR vs AaBee last week".
          </li>
          <li>
            <strong>WhatsApp</strong> — same brain, accessible from your phone.
            Send a message to the Concierge® WA bot and Mira routes it to the
            right intent.
          </li>
        </ol>
      </Section>

      <Section title="The two modes inside the chat tab" icon={<MessageSquare size={13} />}>
        <Choice label="Ask Mira" recommended>
          Read-only intelligence. Strategic analysis, member lookups, outlet
          comparisons, leaderboards, feedback summaries, dashboard-style queries.
          Safe for any leader to use.
        </Choice>
        <Choice label="Operator Mira">
          Read + WRITE. Mira can update Soul data, push CRM fields, draft
          tickets. Use when you want her to TAKE ACTION, not just inform.
        </Choice>
      </Section>

      <Section title="High-value things to ask" icon={<Search size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><strong>"Soul of [member name]"</strong> — pulls their Soul Sheet inline</li>
          <li><strong>"How's [outlet] doing"</strong> — outlet-level marketing pulse</li>
          <li><strong>"Compare JLR vs AaBee last 30 days"</strong> — multi-client compare</li>
          <li><strong>"Leaderboard for outlets"</strong> — ranked top-performers</li>
          <li><strong>"Feedback summary for [outlet]"</strong> — themes from member feedback</li>
          <li><strong>"Update soul of [member]: dietary = vegetarian"</strong> — write to Zoho CRM (Operator mode)</li>
        </ul>
      </Section>

      <Section title="Sharing a conversation" icon={<Send size={13} />}>
        Each chat thread has a <Btn icon={<Send size={11} />} label="Share" /> toggle —
        flip it on and the conversation gets a public read-only URL you can paste
        in a Slack thread or WA group.{' '}
        <strong>Doesn't expose any auth.</strong>
      </Section>

      <Section title="WhatsApp specifics" icon={<Phone size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li>Send a free-form message; Mira classifies the intent and replies in seconds.</li>
          <li>For Soul updates — phrase it as "Update {`{member name}`}: {`{field}`} = {`{value}`}".</li>
          <li>For data entry — Mira reads receipts/photos and parses them into ScaleBoard.</li>
          <li>Every WA call is audit-logged in <code>mira_wa_log</code> — Dipali can review.</li>
        </ul>
      </Section>

      <Section title="When NOT to use Mira" icon={<AlertTriangle size={13} />}>
        <Warn>
          Don't use Mira for high-stakes irreversible actions (deleting members,
          archiving deals, mass emails to clients). Those still go through the
          UI with explicit Approve buttons — the safety nets are there for a reason.
        </Warn>
      </Section>

      <Section title="Permissions" icon={<Lock size={13} />}>
        <ul className="space-y-1 list-disc ml-5">
          <li><Pill bg="#DBEAFE" fg="#1E3A8A">CEO</Pill> — Ask + Operator (read + write)</li>
          <li><Pill bg="#DCFCE7" fg="#166534">Leader / Sub-leader</Pill> — Ask only (read)</li>
        </ul>
      </Section>

      <Section title="Recent updates · May 2026" icon={<RefreshCw size={13} />}>
        <ul className="space-y-1 list-disc ml-5 text-[13px]">
          <li><strong>Indian FY anchor fix</strong> — every YTD / Q1 / contract calculation now starts April 1 (was inflating numbers by 3 months of previous-FY revenue).</li>
          <li><strong>Sync-health endpoint</strong> — Mira's underlying data freshness is checked at <code>/api/admin/ezee/sync-health</code> · returns per-cron verdict + last-run timestamp.</li>
          <li><strong>Auto-clear stale failed sync</strong> — once a fresh CRM/Desk sync succeeds, any earlier failed run for the same client gets superseded automatically. No more red banners after a deploy-restart.</li>
          <li><strong>53 intents · 32-test QA suite</strong> — current intent registry. Every read intent is regression-tested before deploy.</li>
        </ul>
      </Section>

      <Note color="#FFFFFFAA">
        <strong style={{ color: '#7A5C2B' }}>Pro tip:</strong> Mira remembers the
        conversation context. If you ask "how's Goa doing" and then "what about
        Mumbai", she'll keep the same date window and dimension. No need to repeat
        yourself.
      </Note>
    </CheatGuideShell>
  );
}
